import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CtaButtonSignalService } from '@app/finxone-web-frontend/app/lib/services/cta-button-signal-service/cta-button-signal.service';
import { AuthFlowsEnum } from '@finxone-platform/form-action';
import {
  ButtonActionType,
  ExchangeQuotePayload,
  ExchangeQuoteResponse,
  ExchangeTransactionResponse,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, catchError, of, tap } from 'rxjs';
import { setConfirmationToken } from '../../utils/utils';

@Injectable({
  providedIn: 'root',
})
export class ExchangeService {
  private baseUrl = '';
  public lastQuoteResponseData$ = new BehaviorSubject<ExchangeQuoteResponse | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
    private readonly ctaButtonSignalService: CtaButtonSignalService,
    private readonly store: Store,
  ) {
    this.configService.getApi('account_service').subscribe((response) => {
      this.baseUrl = response;
    });
  }

  public rates(rateRequest: { base: string; symbols: string[] }): Observable<{
    base: string;
    rates: {
      [currency: string]: number;
    };
  }> {
    return this.http.post<{
      base: string;
      rates: {
        [currency: string]: number;
      };
    }>(this.baseUrl + '/exchanges/rates', rateRequest);
  }

  public postExchangeQuote(requestData: ExchangeQuotePayload): Observable<ExchangeQuoteResponse> {
    return this.http.post<ExchangeQuoteResponse>(this.baseUrl + '/exchanges/quote', requestData).pipe(
      tap((response) => {
        this.ctaButtonSignalService.setSignal({ [ButtonActionType.EXCHANGE_RE_QUOTE]: false });
        this.lastQuoteResponseData$.next(response); // Store and emit new value
      }),
    );
  }

  public postRefreshQuote(
    quoteId: string,
    requestData: ExchangeQuotePayload,
  ): Observable<ExchangeQuoteResponse> {
    const refreshUrl = `${this.baseUrl + '/exchanges/quote'}/${quoteId}`;
    return this.http.post<ExchangeQuoteResponse>(refreshUrl, requestData).pipe(
      tap((response) => {
        this.ctaButtonSignalService.setSignal({ [ButtonActionType.EXCHANGE_RE_QUOTE]: false });
        this.lastQuoteResponseData$.next(response); // Store and emit new value
      }),
    );
  }

  public postAcceptQuote(quoteId: string): Observable<any> {
    const acceptUrl = `${this.baseUrl}/exchanges/transactions`;
    this.ctaButtonSignalService.setSignal({ [ButtonActionType.EXCHANGE_ACCEPT_QUOTE]: false });
    return this.http.post<any>(acceptUrl, { quoteId }).pipe(
      tap(() => {
        this.ctaButtonSignalService.setSignal({ [ButtonActionType.EXCHANGE_ACCEPT_QUOTE]: true });
      }),
      catchError((error) => {
        this.ctaButtonSignalService.setSignal({ [ButtonActionType.EXCHANGE_ACCEPT_QUOTE]: false });
        console.error('Accept quote failed', error);
        return of(null);
      }),
    );
  }

  public getExchangeDetail(exchangeId: string): Observable<ExchangeTransactionResponse> {
    const url = `${this.baseUrl}/exchanges/transactions/${exchangeId}`;

    return this.http.get<ExchangeTransactionResponse>(url).pipe(
      catchError<any, Observable<boolean>>((_err) => {
        console.error(`Error getting exchange detail for exchangeId ${exchangeId}: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }
}
